import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { workflowService, type Workflow } from '../services/workflowService';
import { Button } from '../components/common/Button';
import { Card, CardContent } from '../components/common/Card';
import { Badge } from '../components/common/Badge';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { useNotification } from '../contexts/NotificationContext';
import WorkflowForm from '../components/admin/WorkflowForm';

export const WorkflowManagement = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentWorkflow, setCurrentWorkflow] = useState<Workflow | null>(null);

  // Fetch workflows
  const { data: workflows = [], isLoading, error } = useQuery({
    queryKey: ['workflows'],
    queryFn: workflowService.getWorkflows
  });

  // Create workflow mutation
  const createWorkflowMutation = useMutation({
    mutationFn: workflowService.createWorkflow,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      setIsCreateModalOpen(false);
      showNotification({
        type: 'success',
        title: 'Success',
        message: 'Workflow created successfully'
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.error || 'Failed to create workflow'
      });
    }
  });

  // Update workflow mutation
  const updateWorkflowMutation = useMutation({
    mutationFn: ({ id, workflow }: { id: number; workflow: Partial<Workflow> }) => 
      workflowService.updateWorkflow(id, workflow),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      setIsEditModalOpen(false);
      showNotification({
        type: 'success',
        title: 'Success',
        message: 'Workflow updated successfully'
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.error || 'Failed to update workflow'
      });
    }
  });

  // Delete workflow mutation
  const deleteWorkflowMutation = useMutation({
    mutationFn: (id: number) => workflowService.deleteWorkflow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      setIsDeleteDialogOpen(false);
      showNotification({
        type: 'success',
        title: 'Success',
        message: 'Workflow deleted successfully'
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.error || 'Failed to delete workflow'
      });
    }
  });

  const handleCreateWorkflow = (workflow: Workflow) => {
    createWorkflowMutation.mutate(workflow);
  };

  const handleUpdateWorkflow = (workflow: Workflow) => {
    if (currentWorkflow?.id) {
      updateWorkflowMutation.mutate({ id: currentWorkflow.id, workflow });
    }
  };

  const handleDeleteWorkflow = () => {
    if (currentWorkflow?.id) {
      deleteWorkflowMutation.mutate(currentWorkflow.id);
    }
  };

  const openEditModal = (workflow: Workflow) => {
    setCurrentWorkflow(workflow);
    setIsEditModalOpen(true);
  };

  const openDeleteDialog = (workflow: Workflow) => {
    setCurrentWorkflow(workflow);
    setIsDeleteDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4">
          Error loading workflows. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Approval Workflows</h1>
        <Button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          New Workflow
        </Button>
      </div>

      {workflows.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-gray-500 mb-4">No workflows found</p>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create First Workflow
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {workflows.map((workflow) => (
            <Card key={workflow.id} className="overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b flex justify-between items-center">
                <h3 className="font-medium truncate">{workflow.name}</h3>
                <Badge color={workflow.is_active ? 'green' : 'gray'}>
                  {workflow.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {workflow.description || 'No description provided'}
                </p>
                
                <div className="flex items-center justify-between text-sm">
                  <div className="text-gray-500">
                    {workflow.steps?.length || 0} steps
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openEditModal(workflow)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => openDeleteDialog(workflow)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Workflow Modal */}
      {isCreateModalOpen && (
        <WorkflowForm
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateWorkflow}
          isLoading={createWorkflowMutation.isPending}
          title="Create Workflow"
        />
      )}

      {/* Edit Workflow Modal */}
      {isEditModalOpen && currentWorkflow && (
        <WorkflowForm
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleUpdateWorkflow}
          isLoading={updateWorkflowMutation.isPending}
          title="Edit Workflow"
          workflow={currentWorkflow}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        title="Delete Workflow"
        message="Are you sure you want to delete this workflow? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteWorkflow}
        onCancel={() => setIsDeleteDialogOpen(false)}
        isLoading={deleteWorkflowMutation.isPending}
        variant="danger"
      />
    </div>
  );
};

export default WorkflowManagement;